import {
  Modal,
  TextInput,
  Group,
  Button,
  Grid,
  Textarea,
  TagsInput,
  Title,
  Stack,
  ActionIcon,
  Tooltip,
} from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import _ from "lodash"
import { zodResolver } from "mantine-form-zod-resolver"
import { useMemo } from "react"

import { eventSchema, EventsRequests } from "@costory/types/endpoints/events"
import { Filters } from "@costory/types/filters"
import { EventCategory } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useTagsQuery } from "@costory/front/queries/events"
import { IconsEventsCategory } from "@costory/front/utils/events"

type Props = {
  data?: EventsRequests.Event
  isOpen: boolean
  onClose: () => void
  onSave: (event: EventsRequests.EventForm) => void
  defaultDate?: string
  defaultCategory?: EventCategory
  currentView?: Filters
}

export const EventsEditModal = ({
  data,
  isOpen,
  onClose,
  onSave,
  defaultDate,
  defaultCategory,
  currentView,
}: Props) => {
  const initialValues = useMemo(() => {
    if (data) {
      return {
        ...eventSchema.parse(data),
        date: dayjs(data.date).format(DEFAULT_DATE_FORMAT),
      }
    }
    return {
      name: "",
      description: "",
      date: defaultDate || dayjs().subtract(1, "d").format(DEFAULT_DATE_FORMAT),
      labels: [],
      tags: [],
      metadata: {},
      category: defaultCategory || EventCategory.BUSINESS,
      viewUsedForCreation: currentView ?? null,
    }
  }, [data, defaultDate, defaultCategory, currentView])
  const form = useForm<EventsRequests.EventForm>({
    initialValues,
    validateInputOnBlur: true,
    validateInputOnChange: true,
    validate: zodResolver(eventSchema),
  })
  const query = useTagsQuery()
  return (
    <Modal
      size="xl"
      opened={isOpen}
      onClose={() => {
        form.reset()
        onClose()
      }}
      withCloseButton={false}
    >
      <form onSubmit={form.onSubmit(onSave)}>
        <Stack>
          <Title order={2}>Custom event</Title>
          <Grid>
            <Grid.Col span={12}>
              <Group justify="flex-start" align="start">
                <ActionIcon.Group>
                  {[EventCategory.BUSINESS, EventCategory.TECHNICAL].map(
                    (el) => (
                      <Tooltip
                        label={_.capitalize(el.toLowerCase() + " events")}
                        key={el}
                        position="bottom"
                      >
                        <ActionIcon
                          variant={
                            form.getValues().category === el
                              ? "filled"
                              : "light"
                          }
                          color={
                            form.getValues().category === el
                              ? "primaryHighlight"
                              : "textMuted"
                          }
                          onClick={() => form.setFieldValue("category", el)}
                          data-intercom-target={`costory-tour-explorer-event-category-${el.toLowerCase()}`}
                        >
                          <IconsEventsCategory category={el} />
                        </ActionIcon>
                      </Tooltip>
                    ),
                  )}
                </ActionIcon.Group>
                <TextInput
                  {...form.getInputProps("name")}
                  placeholder="Event name"
                  miw="75%"
                  data-intercom-target="costory-tour-explorer-event-name-input"
                />
              </Group>
            </Grid.Col>
            <Grid.Col>
              <Textarea
                label="Description"
                {...form.getInputProps("description")}
                data-intercom-target="costory-tour-explorer-event-description"
              />
            </Grid.Col>
            <Grid.Col>
              <DatePickerInput
                label="Date"
                {...form.getInputProps("date")}
                data-intercom-target="costory-tour-explorer-event-date"
              />
            </Grid.Col>
            <Grid.Col>
              <QueryWrapper query={query}>
                {({ data }) => (
                  <TagsInput
                    label="Labels"
                    {...form.getInputProps("labels")}
                    data={
                      data.labels?.map((el) => ({
                        value: el.value,
                        label: el.value,
                      })) ?? []
                    }
                    clearable
                    data-intercom-target="costory-tour-explorer-event-labels"
                  />
                )}
              </QueryWrapper>
            </Grid.Col>
          </Grid>
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            data-intercom-target="costory-tour-explorer-event-save"
          >
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  )
}
